import { Link } from "react-router";
import { breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs.jsx";
import Picture from "../components/Picture/Picture.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import VideoEmbed from "../components/VideoEmbed/VideoEmbed.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import { surfaceFor } from "../lib/surface.js";
import styles from "./bim-3d-modeling.module.css";

const PAGE = {
  title: "3D Project Modeling",
  description:
    "From DGN, DWG, OpenRoads, InfraWorks, Civil 3D or SketchUp to a detailed 3D model of your project, delivered fast.",
  path: "/123bim/3d-modeling",
};
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "3D Visualization", path: "/3d-visualization" },
  { name: "3D Modeling", path: PAGE.path },
];

export function meta() {
  return buildMeta({
    ...PAGE,
    image: "/images/site/123bim/modeling-design",
    jsonLd: [breadcrumbJsonLd(CRUMBS)],
  });
}

// The one switch for this page's light sections: these sit on the light "plan sheet"
// surface from app/styles/surfaces.css. Empty the list to put the whole page back on the
// dark surface; nothing else needs to change.
const LIGHT_SECTIONS = ["alternatives"];
const surface = surfaceFor(LIGHT_SECTIONS);

/**
 * The data a model is built from, bottom to top as it stacks: the existing conditions
 * first, the proposed design laid over them. `design` marks the layer that is the project.
 */
const LAYERS = [
  { name: "Survey and topography", note: "Survey data and topographic models" },
  { name: "GIS", note: "Shape files" },
  { name: "Aerial imagery", note: "Of the site and its surroundings" },
  { name: "Reality mesh", note: "3D reality mesh models" },
  {
    name: "Proposed design",
    note: "DGN, DWG, OpenRoads, InfraWorks, Civil 3D or SketchUp",
    design: true,
  },
];

export default function Bim3dModeling() {
  return (
    <>
      <section className={styles.hero} aria-labelledby="modeling-title">
        <div className={`container ${styles.heroGrid}`}>
          <div className={styles.heroText}>
            <Breadcrumbs items={CRUMBS} />
            <h1 id="modeling-title" className={styles.heroTitle}>
              Your design in 3D, with no change to how you design
            </h1>
            <p className={styles.heroLead}>
              RDV Systems uses its own publishing pipeline to build an interactive 3D model of your
              project, showing the proposed design superimposed on the surrounding existing
              conditions, from the project data you already have.
            </p>
            <div className={styles.heroActions}>
              <Link to="/demo" className="btn btn-primary">
                Request a demo
              </Link>
              <Link to="/3d-visualization" className={styles.heroLink}>
                3D Visualization
              </Link>
            </div>
          </div>
          <div className={styles.heroMedia}>
            <Picture
              src="/images/site/123bim/modeling-design"
              alt="Ray-traced rendering of a proposed roadway design over a river, with the town beyond"
              ratio="16 / 9"
              widths={[480, 960, 1600]}
              fallbackWidth={960}
              sizes="(min-width: 64rem) 50vw, 100vw"
              priority
              className={styles.heroImage}
            />
            <p className={styles.heroChip}>Ray-traced from the design files</p>
          </div>
        </div>
      </section>

      {/* The page's signature: the project data, stacked into the one model it becomes. */}
      <section className="section" aria-labelledby="layers-title">
        <div className="container">
          <SectionHeader
            id="layers-title"
            title="Everything you already have, in one model"
            aside="Existing conditions are modeled from whatever project data already exists, and the proposed design is superimposed on them: a review shows exactly what changes and what stays, not a design floating with no surrounding context."
          />
          <div className={styles.stackRow}>
            <ol className={styles.layers} aria-label="What a model is built from, bottom to top">
              {[...LAYERS].reverse().map((layer) => (
                <li key={layer.name} data-design={layer.design ? "" : undefined}>
                  <span className={styles.slab} aria-hidden="true" />
                  <span className={styles.layerText}>
                    <strong>{layer.name}</strong>
                    <span>{layer.note}</span>
                  </span>
                </li>
              ))}
            </ol>
            <figure className={styles.result}>
              <Picture
                src="/images/site/123bim/modeling-existing"
                alt="A 3D model of existing conditions: the old truss bridge, the river, and the buildings and trees around them"
                ratio="16 / 9"
                widths={[480, 960, 1600]}
                fallbackWidth={960}
                sizes="(min-width: 64rem) 55vw, 100vw"
                className={styles.resultImage}
              />
              <figcaption>
                One interactive model: the existing conditions, before the design goes in.
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section
        className="section"
        data-surface={surface("alternatives")}
        aria-labelledby="alternatives-title"
      >
        <div className={`container ${styles.split}`}>
          <Picture
            src="/images/site/123bim/modeling-brunswick"
            alt="Design alternatives for the Frank J. Wood Bridge near Brunswick, Maine"
            ratio="16 / 9"
            widths={[480, 960, 1600]}
            fallbackWidth={960}
            sizes="(min-width: 64rem) 55vw, 100vw"
            className={styles.splitImage}
          />
          <div className={styles.splitText}>
            <h2 id="alternatives-title">Compare alternatives before you commit</h2>
            <p>
              CAD design data drives the same model at any level of detail, ranging from early
              conceptual layouts to a fully detailed final design, so alternatives can be compared
              before a project commits to one.
            </p>
            <p>
              The Frank J. Wood Bridge replacement near Brunswick, Maine used this workflow to
              render several bridge and roadway alternatives side by side for the design team and
              the public.
            </p>
            <Link to="/3d-visualization#compare" className={styles.heroLink}>
              Compare its existing and proposed views
            </Link>
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="delivery-title">
        <div className={`container ${styles.split} ${styles.delivery}`}>
          <div className={styles.splitText}>
            <h2 id="delivery-title">Rapid delivery</h2>
            <p className={styles.turnaround}>
              <strong>As little as an hour</strong>
              <span>for high-quality ray-traced imagery and video</span>
            </p>
            <p>
              We know how important it is to deliver on time, every time, without any change to a
              project&apos;s existing CAD design process.
            </p>
            <p>
              Traffic simulations, structure styles and aesthetic treatments and 3D models built in
              other applications such as SketchUp or 3ds Max all fold into the same rapid turnaround
              instead of restarting the pipeline for each one.
            </p>
          </div>
          <VideoEmbed
            video="youtube:72TRMEtV0fQ"
            title="3D project modeling"
            poster="/images/video-posters/youtube-72trmetv0fq"
          />
        </div>
      </section>

      <CTABand
        variant="statement"
        image="/images/site/123bim/modeling-brunswick"
        title="Get a model of your design"
        text="Send a DGN, DWG, OpenRoads, InfraWorks, Civil 3D or SketchUp file and get a 3D model back."
        secondary={{ to: "/contact", label: "Talk to our team" }}
      />
    </>
  );
}
